<template>
  <div>
    <!-- 批量订单编辑组件的内容会通过props传入并在父组件中展示 -->
  </div>
</template>

<script setup>
import { ElMessage, ElMessageBox } from 'element-plus';
import axios from 'axios';
import { nextTick } from 'vue';
import { setupOpticalFormInputs } from '@/utils/opticalDataUtil';
import { isAuthRedirecting } from '@/utils/authRedirect';

// eslint-disable-next-line no-unused-vars
const props = defineProps({
  // 是否显示移动端样式
  isMobile: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update', 'cancel']);

// 获取验配单位列表（按当前登录用户，用于编辑弹窗下拉）
const fetchOptometryClinicOptions = async () => {
  try {
    const response = await axios.get('/api/v1/inspection-management/all', {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
    if (response.data.code === '200') {
      const clinics = response.data.data || [];
      // 提取不重复的验配单位名称
      const uniqueClinics = [...new Set(clinics.map(item => item.optometryClinic).filter(Boolean))];
      return uniqueClinics;
    }
  } catch (error) {
    if (error?.response?.status === 401 || isAuthRedirecting()) {
      return null;
    }
    console.error('获取验配单位列表失败:', error);
  }
  return [];
};

// 编辑批量订单
const handleEditBulk = async (row) => {
  try {
    if (isAuthRedirecting()) {
      return;
    }

    // 计算当前行的序号
    const orderIndex = row.index;
    const displayId = orderIndex + 1 + (row.currentPage - 1) * row.pageSize;

    // 获取当前用户的验配单位列表，用于下拉
    const clinicOptions = await fetchOptometryClinicOptions();
    if (clinicOptions === null || isAuthRedirecting()) {
      return;
    }
    const currentCompany = (row.company || '').trim();
    const hasCurrentInList = currentCompany && clinicOptions.includes(currentCompany);
    let companyOptionsHtml = clinicOptions
      .map(c => {
        const value = String(c || '').replace(/"/g, '&quot;');
        const label = String(c || '').replace(/</g, '&lt;');
        const selected = currentCompany === c ? 'selected' : '';
        return `<option value="${value}" ${selected}>${label}</option>`;
      })
      .join('');
    if (currentCompany && !hasCurrentInList) {
      const curValue = currentCompany.replace(/"/g, '&quot;');
      const curLabel = currentCompany.replace(/</g, '&lt;');
      companyOptionsHtml = `<option value="${curValue}" selected>${curLabel}</option>` + companyOptionsHtml;
    }

    await ElMessageBox.confirm(
        `<div class="edit-order-form">
          <div class="form-section basic-info">
            <h3>批量订单基本信息</h3>
            <div class="form-row">
              <div class="form-item">
                <label>序号：</label>
                <input type="text" class="el-input__inner" value="${displayId}" readonly>
                <input type="hidden" id="edit-bulk-id" value="${row.id || ''}">
              </div>
              <div class="form-item">
                <label>订单号：</label>
                <input type="text" class="el-input__inner" value="${row.orderNumber || ''}" id="edit-bulk-orderNumber" readonly>
              </div>
              <div class="form-item">
                <label>用户ID：</label>
                <input type="text" class="el-input__inner" value="${row.userId || ''}" id="edit-bulk-userId" readonly>
              </div>
              <div class="form-item">
                <label>订单日期<span class="required-star">*</span>：</label>
                <input type="text" class="el-input__inner" value="${row.orderDate || ''}" id="edit-bulk-orderDate" readonly>
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
                <label>状态：</label>
                <select class="el-input__inner" id="edit-bulk-status">
                  <option value="Exported" ${row.status === 'Exported' ? 'selected' : ''}>已导出</option>
                <option value="Noexported" ${row.status === 'Noexported' ? 'selected' : ''}>未导出</option>
                </select>
              </div>
              <div class="form-item">
                <label>订单类型：</label>
                <input type="text" class="el-input__inner" value="${row.orderType || '批量订单'}" id="edit-bulk-orderType" readonly>
              </div>
              <div class="form-item">
                <label>地区：</label>
                <input type="text" class="el-input__inner" value="${row.region || ''}" id="edit-bulk-region" required>
              </div>
              <div class="form-item">
                <label>验配单位<span class="required-star">*</span>：</label>
                <select class="el-input__inner" id="edit-bulk-company" required>
                  <option value="">请选择验配单位</option>
                  ${companyOptionsHtml}
                </select>
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
                <label>采购单位<span class="required-star">*</span>：</label>
                <input type="text" class="el-input__inner" value="${row.purchasingUnits || ''}" id="edit-bulk-purchasingUnits" required>
              </div>
              <div class="form-item">
                <label>订单型号<span class="required-star">*</span>：</label>
                <input type="text" class="el-input__inner" value="${row.lensModel || ''}" id="edit-bulk-lensModel" required>
              </div>
              <div class="form-item">
                <label>数量<span class="required-star">*</span>：</label>
                <input type="number" class="el-input__inner numeric-input" value="${row.quantity || ''}" id="edit-bulk-quantity" required>
              </div>
            </div>
          </div>
         <!-- 邮寄信息 -->
        <div class="form-section">
          <h3>邮寄信息</h3>
          <div class="form-row">
            <div class="form-item">
              <label>收件人<span class="required-star">*</span>：</label>
              <input type="text" class="el-input__inner" value="${row.addressee || ''}" id="edit-bulk-addressee">
            </div>
            <div class="form-item">
              <label>联系电话<span class="required-star">*</span>：</label>
              <input type="text" class="el-input__inner" value="${row.phone || ''}" id="edit-bulk-phone">
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>物流编号：</label>
              <input type="text" class="el-input__inner" value="${row.trackingNumber || ''}" id="edit-bulk-trackingNumber">
            </div>
            <div class="form-item">
              <label>邮寄日期：</label>
              <input type="text" class="el-input__inner" value="${row.deliveryDate || ''}" id="edit-bulk-deliveryDate">
            </div>
          </div>
          <div class="form-row">
            <div class="form-item full-width">
              <label>邮寄地址<span class="required-star">*</span>：</label>
              <textarea class="el-textarea__inner" id="edit-bulk-shippingAddress" rows="2">${row.shippingAddress || ''}</textarea>
            </div>
          </div>
          <div class="form-row">
            <div class="form-item full-width">
              <label>备注：</label>
              <textarea class="el-textarea__inner" id="edit-bulk-remarks" rows="2">${row.remarks || ''}</textarea>
            </div>
          </div>
        </div>
      </div>`,
        '编辑批量订单',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          dangerouslyUseHTMLString: true,
          customClass: 'edit-order-dialog'
        }
    );

    if (isAuthRedirecting()) {
      return;
    }

    // 即时绑定数字校验与键盘导航
    nextTick(() => {
      // 使用公用工具函数一次性为当前对话框内所有输入框绑定校验
      setupOpticalFormInputs('edit-bulk');

      // 数量字段：仅允许正整数
      const quantityInput = document.getElementById('edit-bulk-quantity');
      if (quantityInput && !quantityInput.dataset.onlyPositiveAdded) {
        quantityInput.dataset.onlyPositiveAdded = 'true';
        quantityInput.addEventListener('input', (e) => {
          e.target.value = e.target.value.replace(/[^\d]/g, '').replace(/^0+/, '');
        });
      }

      // 设置输入最大长度
      const maxlengthMap = {
        'edit-bulk-region': 50,
        'edit-bulk-company': 30,
        'edit-bulk-purchasingUnits': 30,
        'edit-bulk-lensModel': 30,
        'edit-bulk-quantity': 20,
        'edit-bulk-addressee': 10,
        'edit-bulk-phone': 15
      };
      Object.entries(maxlengthMap).forEach(([id, len]) => {
        const el = document.getElementById(id);
        if (el) el.setAttribute('maxlength', len);
      });
    });

    // 获取所有表单数据
    const bulkOrderData = {
      id: document.getElementById('edit-bulk-id').value,
      userId: document.getElementById('edit-bulk-userId').value,
      orderNumber: document.getElementById('edit-bulk-orderNumber').value,
      status: document.getElementById('edit-bulk-status').value,
      orderDate: document.getElementById('edit-bulk-orderDate').value,
      orderType: document.getElementById('edit-bulk-orderType').value,
      region: document.getElementById('edit-bulk-region').value,
      company: document.getElementById('edit-bulk-company').value,
      purchasingUnits: document.getElementById('edit-bulk-purchasingUnits').value,
      lensModel: document.getElementById('edit-bulk-lensModel').value,
      quantity: document.getElementById('edit-bulk-quantity').value,
      
      // 邮寄信息
      addressee: document.getElementById('edit-bulk-addressee').value || null,
      phone: document.getElementById('edit-bulk-phone').value || null,
      trackingNumber: document.getElementById('edit-bulk-trackingNumber').value || null,
      deliveryDate: document.getElementById('edit-bulk-deliveryDate').value || null,
      shippingAddress: document.getElementById('edit-bulk-shippingAddress').value || null,
      remarks: document.getElementById('edit-bulk-remarks').value || null
    }
    
    const validateBulkFields = (data, rules) => {
      for (const rule of rules) {
        if (!data[rule.field]) {
          ElMessage.error(rule.message);
          return false;
        }
      }
      return true;
    };

    // 定义验证规则
    const bulkValidationRules = [
      { field: 'lensModel', message: '请选择订单型号' },
      { field: 'company', message: '请输入验配单位' },
      { field: 'quantity', message: '请输入数量' },
      { field: 'phone', message: '请输入联系电话'},
      { field: 'addressee', message: '请输入收件人姓名' },
      { field: 'shippingAddress', message: '请输入邮寄地址' },
      { field: 'purchasingUnits', message: '请输入采购单位'}
    ];

    // 执行验证
    if (!validateBulkFields(bulkOrderData, bulkValidationRules)) {
      return;
    }

    // 新增：数量必须为正整数
    if (!/^[1-9]\d*$/.test(bulkOrderData.quantity)) {
      ElMessage.error('数量必须为正整数');
      return;
    }

    console.log('Sending bulk order data:', bulkOrderData);

    const response = await axios.put(`/api/v1/admin/bulkorder`, bulkOrderData);

    if (response.data.code === '200') {
      ElMessage.success('修改成功');
      emit('update');
    } else {
      ElMessage.error(response.data.info || '修改失败');
    }
  } catch (error) {
    if (isAuthRedirecting()) {
      return;
    }
    if (error !== 'cancel') {
      ElMessage.error('修改失败');
    }
    emit('cancel');
  }
};

// 显示批量订单详情
const showBulkOrderDetails = (row) => {
  // 将所有属性格式化为易读的表格
  let content = '<div class="order-details">';
  content += '<h3>订单基本信息</h3>';
  content += '<table class="details-table">';
  content += `<tr><td>订单号</td><td>${row.orderNumber || '-'}</td></tr>`;
  content += `<tr><td>订单类型</td><td>${row.orderType || '-'}</td></tr>`;
  content += `<tr><td>订单状态</td><td>${row.status === 'Exported' ? '已导出' : '未导出'}</td></tr>`;
  content += `<tr><td>地区</td><td>${row.region || '-'}</td></tr>`;
  content += `<tr><td>验光单位</td><td>${row.company || '-'}</td></tr>`;
  content += `<tr><td>采购单位</td><td>${row.purchasingUnits || '-'}</td></tr>`;
  content += `<tr><td>订单型号</td><td>${row.lensModel || '-'}</td></tr>`;
  content += `<tr><td>数量</td><td>${row.quantity || '-'}</td></tr>`;
  content += `<tr><td>创建时间</td><td>${row.createdAt || '-'}</td></tr>`;
  content += `<tr><td>更新时间</td><td>${row.updatedAt || '-'}</td></tr>`;
  content += '</table>';
  
  // 邮寄信息
  content += '<h3>邮寄信息</h3>';
  content += '<table class="details-table">';
  content += `<tr><td>收件人</td><td>${row.addressee || '-'}</td></tr>`;
  content += `<tr><td>联系电话</td><td>${row.phone || '-'}</td></tr>`;
  content += `<tr><td>物流编号</td><td>${row.trackingNumber || '-'}</td></tr>`;
  content += `<tr><td>邮寄地址</td><td>${row.shippingAddress || '-'}</td></tr>`;
  content += `<tr><td>邮寄日期</td><td>${row.deliveryDate || '-'}</td></tr>`;
  content += `<tr><td>备注</td><td>${row.remarks || '-'}</td></tr>`;
  content += '</table>';
  
  content += '</div>';
  
  // 添加详情样式
  content += `<style>
    .el-message-box__container{
      display: block;
    }
    .order-details {
      font-size: 14px;
      padding: 0;
    }
    .order-details h3 {
      margin: 15px 0 8px;
      padding-bottom: 5px;
      border-bottom: 1px solid #EBEEF5;
      color: #409EFF;
      font-size: 16px;
    }
    .details-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 15px;
    }
    .details-table td, .details-table th {
      padding: 8px;
      border: 1px solid #EBEEF5;
      text-align: left;
    }
    .details-table th {
      background-color: #f5f7fa;
      color: #606266;
    }
    .el-message-box.details-dialog {
      width: 50% !important;
      max-width: 1200px !important;
    }
    .el-message-box.details-dialog .el-message-box__content {
      max-height: 70vh;
      overflow-y: auto;
    }
    @media screen and (max-width: 768px) {
      .order-details {
        font-size: 13px;
      }
      .details-table td, .details-table th {
        padding: 5px;
      }
    }
    @media screen and (max-width: 480px) {
      .el-message-box.details-dialog {
       width: 80% !important;
      }
    }
  </style>`;

  // 修改调用方式，添加catch来处理取消操作
  ElMessageBox.alert(content, '批量订单详情', {
    dangerouslyUseHTMLString: true,
    customClass: 'details-dialog',
    closeOnClickModal: true,
    distinguishCancelAndClose: true,
  }).catch(() => {
    // 静默处理取消或关闭操作
    console.log('对话框已关闭');
  });
};

// 批量订单导出Excel
const handleExportBulkExcel = async () => {
  try {
    const response = await axios({
      url: '/api/v1/admin/bulkexcelexport',
      method: 'GET',
      responseType: 'json'
    });

    if (response.data.code === '200' && response.data.data) {
      const binaryString = window.atob(response.data.data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const blob = new Blob([bytes], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const downloadElement = document.createElement('a');
      const href = window.URL.createObjectURL(blob);
      downloadElement.href = href;

      const date = new Date();
      const timestamp = date.getFullYear() +
          ('0' + (date.getMonth() + 1)).slice(-2) +
          ('0' + date.getDate()).slice(-2) +
          ('0' + date.getHours()).slice(-2) +
          ('0' + date.getMinutes()).slice(-2) +
          ('0' + date.getSeconds()).slice(-2);

      downloadElement.download = `批量订单_${timestamp}.xlsx`;
      document.body.appendChild(downloadElement);
      downloadElement.click();
      document.body.removeChild(downloadElement);
      window.URL.revokeObjectURL(href);

      ElMessage.success('导出成功');
      // 导出成功后立即触发红点刷新
      window.dispatchEvent(new CustomEvent('hotspot-refresh'));
      emit('update');
    } else {
      ElMessage.error(response.data.info || '导出失败');
    }
  } catch (error) {
    console.error('导出失败:', error);
    ElMessage.error('导出失败');
  }
};

// 导出选中的批量订单
const handleExportSelectedBulkOrders = async (selectedBulkOrderRows) => {
  if (selectedBulkOrderRows.length === 0) {
    ElMessage.warning('请至少选择一条批量订单');
    return;
  }
  
  // 检查是否包含已导出的订单
  const hasExportedOrders = selectedBulkOrderRows.some(order => order.status === 'Exported');
  
  if (hasExportedOrders) {
    try {
      await ElMessageBox.confirm('选中的批量订单中包含已导出的订单，是否继续导出？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      });
      // 用户确认导出，继续执行
    } catch (e) {
      // 用户取消导出
      return;
    }
  }
  
  // 导出选中的批量订单
  try {
    const orderIds = selectedBulkOrderRows.map(order => order.id);
    
    const response = await axios({
      url: '/api/v1/admin/exportSelectedBulkOrders',
      method: 'POST',
      data: orderIds,
      responseType: 'json'
    });

    if (response.data.code === '200' && response.data.data) {
      const binaryString = window.atob(response.data.data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const blob = new Blob([bytes], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const downloadElement = document.createElement('a');
      const href = window.URL.createObjectURL(blob);
      downloadElement.href = href;

      const date = new Date();
      const timestamp = date.getFullYear() +
          ('0' + (date.getMonth() + 1)).slice(-2) +
          ('0' + date.getDate()).slice(-2) +
          ('0' + date.getHours()).slice(-2) +
          ('0' + date.getMinutes()).slice(-2) +
          ('0' + date.getSeconds()).slice(-2);

      downloadElement.download = `批量订单_${timestamp}.xlsx`;
      document.body.appendChild(downloadElement);
      downloadElement.click();
      document.body.removeChild(downloadElement);
      window.URL.revokeObjectURL(href);

      ElMessage.success('导出成功');
      // 导出成功后立即触发红点刷新
      window.dispatchEvent(new CustomEvent('hotspot-refresh'));
      emit('update');
    } else {
      ElMessage.error(response.data.info || '导出失败');
    }
  } catch (error) {
    console.error('导出失败:', error);
    ElMessage.error('导出失败');
  }
};

// 暴露函数给父组件使用
defineExpose({
  handleEditBulk,
  showBulkOrderDetails,
  handleExportBulkExcel,
  handleExportSelectedBulkOrders
});
</script> 
